import angleIsBetween from './angleIsBetween';

// The high level type here is the cones.
// The width (or angle range) of each cone is 360/coneCount
// Each cone consists of a set of segments, each segment representing a binned value range, derived from (binCount & binStepSize)
// For each magnitude that exists within a cone's angle range, use the magnitude to know what segment the datum fits within.
const pointsToCones = ({
    points,
    coneCount,
    binCount,
    binStepSize,
}: {
    points: { angle: number; magnitude: number }[];
    coneCount: number;
    binCount: number;
    binStepSize: number;
}) => {
    const coneAngleSize = 360 / coneCount;

    return (
        points
            .reduce(
                (cones, point) => {
                    const coneForDirection = cones.find((cone) => angleIsBetween(point.angle, cone.angleRange.min, cone.angleRange.max))!;
                    return [
                        ...cones.filter((c) => c.id !== coneForDirection.id),
                        {
                            ...coneForDirection,
                            magnitudes: [...coneForDirection.magnitudes, point.magnitude],
                        },
                    ];
                },
                Array.from({ length: coneCount }, (_, i) => {
                    return {
                        id: i,
                        angleRange: {
                            min: (i / coneCount) * 360 - coneAngleSize / 2,
                            max: (i / coneCount) * 360 + coneAngleSize / 2,
                        },
                        magnitudes: [],
                        segments: Array.from({ length: binCount }, (_, i) => {
                            return {
                                bin: i,
                                magnitudeRange: {
                                    min: binStepSize * i,
                                    max: i !== binCount - 1 ? binStepSize * i + binStepSize : Infinity, //Infinity for the last bin to catch all numbers above, eg 8+
                                },
                                length: 0,
                            };
                        }),
                    };
                })
            )
            //For each cone there is a set of segments to represent the binned magnitudes
            //Here we calculate those segments, by iterating over every bin, and finding any magnitude that fits within that bin, then adding it
            .map((cone) => {
                return {
                    ...cone,
                    segments: cone.segments.map((segment) => {
                        const magnitudesForSegment = cone.magnitudes.filter((magnitude) => {
                            if (magnitude < segment.magnitudeRange.min) {
                                return false;
                            }
                            if (magnitude > segment.magnitudeRange.max) {
                                return false;
                            }
                            return true;
                        });
                        return {
                            ...segment,
                            length: magnitudesForSegment.length / points.length,
                        };
                    }),
                };
            })
            .sort((a, b) => (a.id > b.id ? 1 : -1))
    );
};

export default pointsToCones;
